import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { cloneDeep, throttle } from 'lodash';
import md5 from 'md5';
import { useAcl, useI18n, usePlanLimits, useReportForm, useReportUtils, useTutorialIntro, useTutorialApi, useFlowNavigationErrors, useMixpanel, useWorkspace, type PlanLimitsProp } from '@/composables';
import { $emitter } from '@/services/emitter';
import helpers from '@/services/helpers';
import {
  getCampaignApiRequest,
  postCreateCampaignApiRequest,
  putUpdateCampaignApiRequest,
} from '@/services/api/campaign-api';
import { getFlowsListApiRequest } from '@/services/api/flows-api';
import { getLandingsListApiRequest } from '@/services/api/landings-api';
import { getOffersListApiRequest } from '@/services/api/offers-api';
import { populateFlowsStoreCacheApiRequest, updateFlowsStoreCacheApiRequest } from '@/services/api/api-services/flows';
import { populateLandingsStoreCacheApiRequest, updateLandingsStoreCacheApiRequest } from '@/services/api/api-services/landings';
import { populateOffersStoreCacheApiRequest, updateOffersStoreCacheApiRequest } from '@/services/api/api-services/offers';
import { populateTrafficSourcesStoreCacheApiRequest } from '@/services/api/api-services/traffic-sources';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import formMock from './config/form-mock';
import macroMock from './config/macro-mock';
import { useStore } from '@/store';
import {
  populateRedirectDomainsStoreCacheApiRequest,
  populateTrackingDomainsStoreCacheApiRequest,
} from '@/services/api/api-services/domains';
import { openCampaignLinksDialog } from '@/views/reports/forms/campaign/campaign-menu-dialog-actions';
import {
  applyFlowSubmitErrorsFromResponse,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';
import type { FieldErrors } from '@/services/models';

export type CampaignDialogMode = 'create' | 'edit' | 'duplicate';

export type CampaignDialogData = {
  mode?: CampaignDialogMode;
  id?: string;
  [key: string]: unknown;
};

type CampaignForm = Record<string, unknown> & {
  workspaceId?: string | null;
  countryId?: number;
  destinationType?: string;
  destinationUrl?: string;
  rotation?: string;
  redirectMode?: string;
  redirectDomainId?: string;
  flow?: Flow | null;
  flowInline?: {
    rules: Array<{
      paths: Array<{
        landings: Array<{ id?: string }>;
        offers: Array<{ id?: string }>;
        directLinking?: boolean;
        [key: string]: unknown;
      }>;
      conditions?: Record<string, unknown>;
      [key: string]: unknown;
    }>;
  };
  trafficSource?: { id: string; name: string };
  tags?: Array<{ id?: string; name: string } | string>;
  trackingDomainId?: string;
  trafficLossPercent?: number | null;
  postbackPercent?: number | null;
  useTrafficLoss?: boolean;
  usePostback?: boolean;
  sendCustomConversionMode?: string;
  sendCustomConversionIds?: string[];
  doNotSendNotSpecifiedConversions?: boolean;
  sendCustomEventPostback?: unknown;
};

const defaultForm = (): CampaignForm => ({
  ...formMock(),
  currencyId: useStore().user.currencyId,
  rotation: '',
});

export const useCampaignDialog = (
  mode: CampaignDialogMode,
  getData: () => CampaignDialogData,
  emitClose: () => void,
) => {
  const store = useStore();
  const workspaceApi = useWorkspace();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const planLimitsControl = usePlanLimits();
  const { trackMixpanel } = useMixpanel();

  const reportUtilsApi = useReportUtils({});

  const edit = ref(mode === 'edit');
  const show = ref(false);
  const step = ref(1);
  const campaign_id = ref('');
  const oldHash = ref('');
  const emptyUrlSend = ref(false);
  const flowInlineRenderedOnce = ref(false);
  const lastTrackingDomainId = ref('');
  const selectTrackingDomain = ref<unknown>([]);
  const form = ref<CampaignForm>(defaultForm());
  const chips = ref<Array<{ tag: string; disabled: boolean }>>([]);
  const flowOptions = ref<Flow[]>([]);
  const offerOptions = ref<Offer[]>([]);
  const landingOptions = ref<Landing[]>([]);
  const errorsRaw = ref<unknown[][] | null>(null);
  const errorMessage = ref('');
  const fieldErrors = ref<FieldErrors>({});
  const errorsControlActive = ref(false);
  const campaignInitialState = ref<Campaign>({} as Campaign);
  const scrollPanelDestinationRef = ref<HTMLElement | null>(null);
  const flowInlineErrorPrefix = ref(['flowInline']);
  const flowPathErrors = useFlowNavigationErrors(errorsRaw, flowInlineErrorPrefix);

  const reportForm = useReportForm(() => ({
    edit: edit.value,
    mode: edit.value ? 'edit' : 'create',
  }));

  const workspacesList = computed(() => reportForm.workspacesList.value);
  const defaultWorkspace = computed(() => reportForm.defaultWorkspace.value);
  const rotationOptions = computed(() => reportForm.rotationOptions.value);

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of campaign')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Select a traffic source')}</div>`,
    4: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Select URL as campaign destination')}</div>`,
    5: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Specify the URL which will be the endpoint of your traffic')}</div>`,
    6: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Create campaign')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'campaigns',
  }));

  const tutorial = useTutorialApi();

  const modalTitle = computed(() => {
    if (mode === 'edit') return t('Edit Campaign');
    if (mode === 'duplicate') return t('Duplicate Campaign');
    return t('Create Campaign');
  });

  const translateErrorsConfig = computed(() => {
    const translate: Record<string, Record<string, string>> = {
      flowId: {
        VALIDATION_INVALID_VALUE: 'CUSTOM_VALIDATION_FLOW_SHOULD_BE_SELECTED',
      },
    };

    if (emptyUrlSend.value) {
      translate.destinationUrl = {
        VALIDATION_INVALID_URL: 'CUSTOM_VALIDATION_EMPTY_URL',
      };
    }

    return { translate };
  });

  const errors = computed(() =>
    translateFlowErrorsRaw(errorsRaw.value, errorMessage.value, translateErrorsConfig.value),
  );

  const errorsDict = computed(() => fieldErrors.value as Record<string, string[]>);

  const destinationUrl = computed({
    get: () => String(form.value.destinationUrl || ''),
    set: (value: string) => {
      form.value = {
        ...form.value,
        destinationUrl: value,
      };
    },
  });

  const isActionsDisabled = computed(() => {
    if (!edit.value) return false;
    return Boolean(
      aclReadOnly({
        workspaceId: form.value.workspaceId,
        id: campaign_id.value,
      }),
    );
  });

  const convertToFlowDisabled = computed(() => form.value.rotation !== 'random');

  const isDisabledWeight = computed(
    () => rotationOptions.value[0]?.key !== form.value.rotation,
  );

  const allWorkspaceIds = computed(() => [
    ...new Set([form.value.workspaceId, ...workspaceApi.allPublicWorkspacesIds.value]),
  ]);

  const showPlanLimitsWarning_rotation = computed(
    () =>
      form.value.rotation !== 'random'
      && planLimitsControl.hasPlanLimitsRestriction('smartRotations'),
  );

  const showPlanLimitsWarning_doubleMeta = computed(
    () =>
      form.value.redirectMode === 'doubleMeta'
      && planLimitsControl.hasPlanLimitsRestriction('doubleMetaRedirect'),
  );

  const fetchFlowOptions = computed(() => {
    if (store.cache.flows.isAllInCache) return undefined;
    return ({ search }: { search: string }) =>
      getFlowsListApiRequest({
        search,
        status: 'active',
        limit: '200',
        orderBy: 'updated',
        orderDirection: 'desc',
        columns: ['name', 'nameFull', 'id', 'countryId', 'workspaceId'],
        workspaceId: form.value.workspaceId,
      });
  });

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const countryCondition = ({
    form: campaignForm,
    item,
    id,
  }: {
    form: { countryId?: number };
    item: { countryId?: number; id?: string };
    id?: string;
  }) => {
    const countryId = campaignForm.countryId;
    return (
      countryId === 0
      || item.countryId === 0
      || countryId === item.countryId
      || Boolean(id && item.id === id)
    );
  };

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const clearSubmitErrors = () => {
    errorsRaw.value = null;
    errorMessage.value = '';
    fieldErrors.value = {};
    errorsControlActive.value = false;
  };

  const applySubmitErrors = (error: unknown) => {
    const parsed = applyFlowSubmitErrorsFromResponse(error);
    if (!parsed) return;
    errorsRaw.value = parsed.errorsRaw;
    errorMessage.value = parsed.errorMessage;
    fieldErrors.value = parsed.fieldErrors;
    errorsControlActive.value = true;
  };

  const normalizeCampaignFromApi = (campaign: Record<string, unknown>) => {
    const normalized = {
      ...campaign,
      useTrafficLoss: campaign.trafficLossPercent !== null,
      usePostback: campaign.postbackPercent !== null,
      botFiltersMode: campaign.botFiltersMode || 0,
      trackingDomainId: campaign.trackingDomainId || '',
      trafficSource: campaign.trafficSource || {
        name: t('None'),
        id: '',
      },
      sendCustomConversionMode:
        (campaign.sendCustomEventPostback as { mode?: string })?.mode || 'all',
      sendCustomConversionIds:
        (campaign.sendCustomEventPostback as { ids?: string[] })?.ids || [],
      doNotSendNotSpecifiedConversions:
        (campaign.sendCustomEventPostback as { doNotSendNotSpecifiedConversions?: boolean })
          ?.doNotSendNotSpecifiedConversions || false,
      maxCostEnabled: !!campaign.maxCostEnabled,
      maxCostUsd: campaign.maxCostUsd ?? null,
      maxCostAction: campaign.maxCostEnabled
        ? (campaign.maxCostAction ?? 1)
        : 1,
      maxCostBotOfferId: campaign.maxCostBotOfferId || null,
    } as CampaignForm;

    delete normalized.sendCustomEventPostback;
    return normalized;
  };

  const setFlowOptions = async () => {
    const { uploadedForWorkspace, uploadedForCountryId, isAllInCache, data, isUploaded } =
      store.cache.flows;

    if (isUploaded && isAllInCache) {
      flowOptions.value = data.filter(
        item =>
          (workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
            || item.workspaceId === form.value.workspaceId)
          && countryCondition({
            form: form.value,
            item,
            id: campaignInitialState.value.flow?.id,
          }),
      );
      return;
    }

    if (
      isUploaded
      && !isAllInCache
      && uploadedForWorkspace === form.value.workspaceId
      && uploadedForCountryId === form.value.countryId
    ) {
      flowOptions.value = [...data];
      return;
    }

    if (
      form.value.workspaceId === defaultWorkspace.value.id
      && form.value.countryId === 0
    ) {
      flowOptions.value = (await updateFlowsStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      })) as Flow[];
      return;
    }

    flowOptions.value = (await getFlowsListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: ['name', 'nameFull', 'id', 'countryId', 'workspaceId'],
      workspaceId: form.value.workspaceId,
      ...(form.value.countryId !== 0 && { countryId: form.value.countryId }),
    })) as Flow[];
  };

  const setOfferOptions = async () => {
    const { uploadedForWorkspace, uploadedForCountryId, isAllInCache, data, isUploaded } =
      store.cache.offers;

    if (isUploaded && isAllInCache) {
      offerOptions.value = data.filter(
        item =>
          (workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
            || item.workspaceId === form.value.workspaceId)
          && countryCondition({ form: form.value, item }),
      );
      return;
    }

    if (
      isUploaded
      && !isAllInCache
      && uploadedForWorkspace === form.value.workspaceId
      && uploadedForCountryId === form.value.countryId
    ) {
      offerOptions.value = [...data];
      return;
    }

    if (
      form.value.workspaceId === defaultWorkspace.value.id
      && form.value.countryId === 0
    ) {
      offerOptions.value = (await updateOffersStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      })) as Offer[];
      return;
    }

    offerOptions.value = (await getOffersListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: [
        'id',
        'name',
        'nameFull',
        'countryId',
        'affiliateNetworkId',
        'affiliateNetworkName',
        'countryName',
        'url',
      ],
      workspaceId: form.value.workspaceId,
      ...(form.value.countryId !== 0 && { countryId: form.value.countryId }),
    })) as Offer[];
  };

  const setLandingOptions = async () => {
    const { uploadedForWorkspace, uploadedForCountryId, isAllInCache, data, isUploaded } =
      store.cache.landings;

    if (isUploaded && isAllInCache) {
      landingOptions.value = data.filter(
        item =>
          (workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
            || item.workspaceId === form.value.workspaceId)
          && countryCondition({ form: form.value, item }),
      );
      return;
    }

    if (
      isUploaded
      && !isAllInCache
      && uploadedForWorkspace === form.value.workspaceId
      && uploadedForCountryId === form.value.countryId
    ) {
      landingOptions.value = [...data];
      return;
    }

    if (
      form.value.workspaceId === defaultWorkspace.value.id
      && form.value.countryId === 0
    ) {
      landingOptions.value = (await updateLandingsStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      })) as Landing[];
      return;
    }

    landingOptions.value = (await getLandingsListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: ['name', 'nameFull', 'id', 'countryId', 'workspaceId'],
      workspaceId: form.value.workspaceId,
      ...(form.value.countryId !== 0 && { countryId: form.value.countryId }),
    })) as Landing[];
  };

  const loadCreateForm = async () => {
    campaign_id.value = '';
    form.value = {
      ...defaultForm(),
      rotation: rotationOptions.value[0]?.key,
      workspaceId: defaultWorkspace.value.id,
    };
    oldHash.value = getCurrentHash();
  };

  const loadEditForm = async () => {
    const data = getData();
    campaign_id.value = data.id || (store.getTableSelectedItem().id as string);
    const campaign = await getCampaignApiRequest(campaign_id.value);
    edit.value = true;
    form.value = normalizeCampaignFromApi(campaign as Record<string, unknown>);
    campaignInitialState.value = campaign;
    oldHash.value = getCurrentHash();
  };

  const loadDuplicateForm = async () => {
    campaign_id.value = getData().id || (store.getTableSelectedItem().id as string);
    const campaign = await getCampaignApiRequest(campaign_id.value);
    campaign.name = `${campaign.name} (${t('____x.copy')})`;
    form.value = normalizeCampaignFromApi(campaign as Record<string, unknown>);
    oldHash.value = getCurrentHash();
  };

  const destinationTypeChange = () => {
    form.value.destinationUrl = '';
    form.value.flow = null;
    form.value.rotation = rotationOptions.value[0]?.key;
    form.value.flowInline = {
      ...formMock().flowInline,
    };
  };

  const changeTrackingDomain = (value: string) => {
    form.value.trackingDomainId = value;

    if (form.value.trackingDomainId === lastTrackingDomainId.value) return;

    if (form.value.trackingDomainId === null) {
      form.value.trackingDomainId = '';
    }

    const trackingDomainId =
      form.value.trackingDomainId || store.user.trackingDomainId;

    selectTrackingDomain.value = store.trackingDomainsObject[trackingDomainId];
    lastTrackingDomainId.value = String(form.value.trackingDomainId || '');
  };

  const onChangeCountry = () => {
    setFlowOptions();
    setLandingOptions();
    setOfferOptions();
  };

  const onChangeWorkspace = () => {
    const { flowInline, trackingDomainId, flow } = defaultForm();
    form.value = {
      ...form.value,
      flowInline,
      flow,
      trackingDomainId,
    };
    setFlowOptions();
    setLandingOptions();
    setOfferOptions();
  };

  const updateLanding = (updatedLanding: Landing) => {
    const index = landingOptions.value.findIndex(item => item.id === updatedLanding.id);
    if (index !== -1) {
      landingOptions.value[index] = updatedLanding;
    }

    form.value.flowInline?.rules.forEach((rule, ruleIndex) => {
      rule.paths.forEach((path, pathIndex) => {
        path.landings.forEach((landing, landingIndex) => {
          if (landing.id === updatedLanding.id) {
            const flowInline = form.value.flowInline;
            if (!flowInline) return;
            flowInline.rules[ruleIndex].paths[pathIndex].landings[landingIndex] = {
              ...landing,
              ...updatedLanding,
            };
          }
        });
      });
    });
  };

  const updateOffer = (updatedOffer: Offer) => {
    const index = offerOptions.value.findIndex(item => item.id === updatedOffer.id);
    if (index !== -1) {
      offerOptions.value[index] = updatedOffer;
    }

    form.value.flowInline?.rules.forEach((rule, ruleIndex) => {
      rule.paths.forEach((path, pathIndex) => {
        path.offers.forEach((offer, offerIndex) => {
          if (offer.id === updatedOffer.id) {
            const flowInline = form.value.flowInline;
            if (!flowInline) return;
            flowInline.rules[ruleIndex].paths[pathIndex].offers[offerIndex] = {
              ...offer,
              ...updatedOffer,
            };
          }
        });
      });
    });
  };

  const updateFlow = (updatedFlow: Flow) => {
    flowOptions.value = flowOptions.value.map((item: Flow) =>
      item.id === updatedFlow.id ? updatedFlow : item,
    );

    if (form.value.flow?.id === updatedFlow.id) {
      form.value.flow = { ...updatedFlow };
    }
  };

  const openCreateFlowDialog = () => {
    const rulesWithUpdatedRedirectMode = (form.value.flowInline?.rules || []).map(rule => ({
      ...rule,
      paths: rule.paths.map(path => ({
        ...path,
        ...(form.value.redirectMode === 'doubleMeta' && {
          redirectDomainId: form.value.redirectDomainId,
          redirectDomainIdOffers: form.value.redirectDomainId,
        }),
        redirectMode: form.value.redirectMode,
        redirectModeOffers: form.value.redirectMode,
      })),
    }));

    $emitter.emit('open-dialog', {
      name: 'FlowDialog',
      data: {
        mode: 'create',
        createFromConvertToFlow: true,
        form: {
          workspaceId: form.value.workspaceId,
          rules: rulesWithUpdatedRedirectMode,
        },
        onConfirm: (flow: Flow) => {
          flowOptions.value.unshift(flow);
          form.value.destinationType = 'flow';
          form.value.flow = flow;
        },
      },
    });
  };

  const addNewRule = (rule?: unknown) => {
    $emitter.emit('open-dialog', {
      name: 'CreateRule',
      data: { rule, target: { form: form.value } },
    });
  };

  const processForm = (source: CampaignForm) => {
    const result = cloneDeep(source) as CampaignForm & {
      sendCustomEventPostback?: unknown;
      trafficSourceId?: string;
      flowId?: string | null;
    };

    result.sendCustomEventPostback = {
      mode: result.sendCustomConversionMode,
      ...(result.sendCustomConversionMode !== 'all'
        ? { ids: result.sendCustomConversionIds }
        : {}),
      doNotSendNotSpecifiedConversions: result.doNotSendNotSpecifiedConversions,
    };

    result.trafficSourceId = result.trafficSource?.id;
    result.flowId = result.flow?.id || null;
    result.tags = result?.tags?.map(item =>
      item && typeof item === 'object' && 'id' in item && item.id ? item.name : item,
    ) as CampaignForm['tags'];

    delete result.sendCustomConversionMode;
    delete result.sendCustomConversionIds;
    delete result.doNotSendNotSpecifiedConversions;
    delete result.trafficSource;
    delete result.flow;

    if (source.flowInline) {
      result.flowInline?.rules.forEach(rule => {
        rule.paths.forEach(path => {
          if (path.directLinking) {
            path.landings = path.landings.filter(item => item.id);
          }
        });
      });
    }

    if (!result.maxCostEnabled || !['auto', 'autoCpm'].includes(String(result.costModel))) {
      delete result.maxCostEnabled;
      delete result.maxCostUsd;
      delete result.maxCostAction;
      delete result.maxCostBotOfferId;
    } else {
      result.maxCostUsd = Number(result.maxCostUsd) || null;
      if (result.maxCostAction !== 0) {
        delete result.maxCostBotOfferId;
      } else {
        result.maxCostBotOfferId = result.maxCostBotOfferId || null;
      }
    }

    return result;
  };

  const trackAnalytics = (event: string, payload: CampaignForm) => {
    const { postbackPercent, trafficLossPercent, uniquenessPeriod, redirectMode, name } = payload;
    let rules: string[] = [];

    if (payload.destinationType === 'flowInline' && payload.flowInline) {
      rules = payload.flowInline.rules.reduce((accumulator: string[], rule) => {
        Object.keys(rule.conditions || {}).forEach(condition => accumulator.push(condition));
        return accumulator;
      }, []);
    }

    trackMixpanel(event, {
      name,
      ...(payload.usePostback && { postback_percent: postbackPercent }),
      ...(payload.useTrafficLoss && { trafficLoss_percent: trafficLossPercent }),
      uniqueness_period: uniquenessPeriod,
      ...((payload.destinationType === 'flowInline' || payload.destinationType === 'flow')
        && { redirect_mode: redirectMode }),
      ...(payload.destinationType === 'flowInline' && { rules }),
    });
  };

  const close = () => {
    unsubscribeEventBus();
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const onBusIntroJsOnbeforechange = ({
    introInstance: introJsInstance,
    callback,
  }: {
    introInstance: { _currentStep: number; _direction: string; goToStepNumber: (step: number) => void };
    callback: (value: boolean) => void;
  }) => {
    const isCurrentTutorial =
      store.tutorial.quickStartSection === 'campaigns' && store.tutorial.quickStartStep;

    if (isCurrentTutorial && form.value.destinationType !== 'url' && introJsInstance._currentStep === 3) {
      const direction = introJsInstance._direction;
      nextTick(() => {
        if (direction === 'forward') {
          introJsInstance.goToStepNumber(6);
        } else {
          introJsInstance.goToStepNumber(4);
        }
      });
      callback(false);
    }
  };

  const onBusCampaignDeleteLanding = (
    ruleIndex: number,
    pathIndex: number,
    landingIndex: number,
  ) => {
    if (!errorsRaw.value) return;

    const landingErrors = flowPathErrors.processLandingError(ruleIndex, pathIndex, landingIndex);
    errorsRaw.value = flowPathErrors.fixLandingErrorIndexes(
      ruleIndex,
      pathIndex,
      landingIndex,
      landingErrors,
    );
  };

  const onBusCampaignDeleteOffer = (
    ruleIndex: number,
    pathIndex: number,
    offerIndex: number,
  ) => {
    if (!errorsRaw.value) return;

    const offerErrors = flowPathErrors.processOfferError(ruleIndex, pathIndex, offerIndex);
    errorsRaw.value = flowPathErrors.fixOfferErrorIndexes(
      ruleIndex,
      pathIndex,
      offerIndex,
      offerErrors,
    );
  };

  const onBusCampaignCampaignDeleteRule = (ruleIndex: number) => {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('All selected items will be deleted permanently if you press OK.'),
        onConfirm: () => {
          if (errorsRaw.value) {
            const ruleErrors = flowPathErrors.processRuleError(ruleIndex);
            errorsRaw.value = flowPathErrors.fixRuleErrorIndexes(ruleIndex, ruleErrors);
          }
          form.value.flowInline?.rules.splice(ruleIndex, 1);
        },
      },
    });
  };

  const onBusCampaignCampaignDeletePath = (ruleIndex: number, pathIndex: number) => {
    const rule = form.value.flowInline?.rules[ruleIndex];
    if (!rule) return;

    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('All selected items will be deleted permanently if you press OK.'),
        onConfirm: () => {
          if (errorsRaw.value) {
            const pathErrors = flowPathErrors.processPathError(ruleIndex, pathIndex);
            errorsRaw.value = flowPathErrors.fixPathErrorIndexes(
              ruleIndex,
              pathIndex,
              pathErrors,
            );
          }
          rule.paths.splice(pathIndex, 1);
        },
      },
    });
  };

  const onBusCampaignNewLanding = () => {
    $emitter.emit('open-dialog', {
      name: 'LandingDialog',
      data: {
        mode: 'create',
        workspaceId: form.value.workspaceId,
        onClose: setLandingOptions,
      },
    });
  };

  const onBusCampaignEditLanding = (id: string) => {
    $emitter.emit('open-dialog', {
      name: 'LandingDialog',
      data: {
        mode: 'edit',
        id,
        onEdit: updateLanding,
      },
    });
  };

  const onBusCampaignNewOffer = () => {
    $emitter.emit('open-dialog', {
      name: 'OfferDialog',
      data: {
        mode: 'create',
        workspaceId: form.value.workspaceId,
        onClose: setOfferOptions,
      },
    });
  };

  const onBusCampaignEditOffer = (id: string) => {
    $emitter.emit('open-dialog', {
      name: 'OfferDialog',
      data: {
        mode: 'edit',
        id,
        onEdit: updateOffer,
      },
    });
  };

  const subscribeEventBus = () => {
    $emitter.on('campaign-delete-rule', onBusCampaignCampaignDeleteRule);
    $emitter.on('campaign-delete-path', onBusCampaignCampaignDeletePath);
    $emitter.on('campaign-new-landing', onBusCampaignNewLanding);
    $emitter.on('campaign-edit-landing', onBusCampaignEditLanding);
    $emitter.on('campaign-delete-landing', onBusCampaignDeleteLanding);
    $emitter.on('campaign-new-offer', onBusCampaignNewOffer);
    $emitter.on('campaign-edit-offer', onBusCampaignEditOffer);
    $emitter.on('campaign-delete-offer', onBusCampaignDeleteOffer);
    $emitter.on('intro-js-onbeforechange', onBusIntroJsOnbeforechange);
  };

  const unsubscribeEventBus = () => {
    $emitter.off('campaign-delete-rule', onBusCampaignCampaignDeleteRule);
    $emitter.off('campaign-delete-path', onBusCampaignCampaignDeletePath);
    $emitter.off('campaign-new-landing', onBusCampaignNewLanding);
    $emitter.off('campaign-edit-landing', onBusCampaignEditLanding);
    $emitter.off('campaign-delete-landing', onBusCampaignDeleteLanding);
    $emitter.off('campaign-new-offer', onBusCampaignNewOffer);
    $emitter.off('campaign-edit-offer', onBusCampaignEditOffer);
    $emitter.off('campaign-delete-offer', onBusCampaignDeleteOffer);
    $emitter.off('intro-js-onbeforechange', onBusIntroJsOnbeforechange);
  };

  const submit = async (forceOpenLinks = false) => {
    store.setLoading(true);
    emptyUrlSend.value = !form.value.destinationUrl;
    clearSubmitErrors();

    const payload = processForm(form.value);

    try {
      if (mode === 'edit') {
        const result = await putUpdateCampaignApiRequest(campaign_id.value, payload);
        const { id } = result;
        reportUtilsApi.updateName('campaigns', id as string);
        await refreshUserProfileFromApi();
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Campaign has been changed'));
        trackAnalytics('campaign_edit', payload);
        close();
        if (forceOpenLinks) {
          openCampaignLinksDialog(id as string);
        }
        return;
      }

      const result = await postCreateCampaignApiRequest(payload);
      const { id } = result;
      trackAnalytics('campaign_create', payload);
      await refreshUserProfileFromApi();

      setTimeout(() => {
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Campaign has been saved'));
        close();
        if (forceOpenLinks) {
          openCampaignLinksDialog(id as string);
        }
        store.setLoading(false);
      }, 1000);
    } catch (error) {
      applySubmitErrors(error);
      store.setLoading(false);
    }
  };

  const save = () => {
    submit(true);
    tutorial.close();
  };

  const onDialogScroll = () => {
    $emitter.emit('dialog-scroll', 'campaigns-dialog');
  };

  const onDialogScrollThrottled = throttle(onDialogScroll, 70);

  watch(
    () => form.value.destinationType,
    value => {
      if (value === 'flowInline' && !flowInlineRenderedOnce.value) {
        flowInlineRenderedOnce.value = true;
      }
    },
    { immediate: true },
  );

  watch(show, value => {
    helpers.afterBrowserRedraw(() => {
      const scrollPanel = scrollPanelDestinationRef.value;
      if (!scrollPanel) return;
      if (value) {
        scrollPanel.addEventListener('scroll', onDialogScrollThrottled);
      } else {
        scrollPanel.removeEventListener('scroll', onDialogScrollThrottled);
      }
    });
  });

  onMounted(async () => {
    chips.value = macroMock().map(tag => ({
      tag,
      disabled: false,
    }));

    subscribeEventBus();
    store.setLoading(true);

    try {
      if (mode === 'edit') {
        await loadEditForm();
      } else if (mode === 'duplicate') {
        await loadDuplicateForm();
      } else {
        await loadCreateForm();
      }

      if (!store.cache.trafficSources.isUploaded) {
        await populateTrafficSourcesStoreCacheApiRequest();
      }

      if (!store.cache.flows.isUploaded) {
        await populateFlowsStoreCacheApiRequest();
      }

      if (!store.cache.landings.isUploaded) {
        await populateLandingsStoreCacheApiRequest();
      }

      if (!store.cache.offers.isUploaded) {
        await populateOffersStoreCacheApiRequest();
      }

      if (!store.trackingDomains.isUploaded) {
        await populateTrackingDomainsStoreCacheApiRequest();
      }

      if (!store.redirectDomains.isUploaded) {
        await populateRedirectDomainsStoreCacheApiRequest();
      }

      if (!store.cache.tags.campaigns.isUploaded) {
        await populateTagsStoreCacheApiRequest({ type: 'campaigns' });
      }

      tutorial.form();
      show.value = true;
      await setFlowOptions();
      await setLandingOptions();
      await setOfferOptions();
    } finally {
      store.setLoading(false);
    }
  });

  onUnmounted(() => {
    unsubscribeEventBus();
    const scrollPanel = scrollPanelDestinationRef.value;
    if (scrollPanel) {
      scrollPanel.removeEventListener('scroll', onDialogScrollThrottled);
    }
  });

  return {
    store,

    t,
    show,
    step,
    form,
    edit,
    chips,
    flowOptions,
    offerOptions,
    landingOptions,
    errorsRaw,
    errors,
    errorsControlActive,
    errorsDict,
    campaignInitialState,
    scrollPanelDestinationRef,
    workspacesList,
    defaultWorkspace,
    rotationOptions,
    modalTitle,
    destinationUrl,
    isActionsDisabled,
    convertToFlowDisabled,
    isDisabledWeight,
    allWorkspaceIds,
    showPlanLimitsWarning_rotation,
    showPlanLimitsWarning_doubleMeta,
    fetchFlowOptions,
    flowInlineRenderedOnce,
    defaultForm,
    getIntroParams: tutorialIntro.getIntroParams,
    hasPlanLimitsRestriction,
    checkClose,
    close,
    changeTrackingDomain,
    onChangeWorkspace,
    onChangeCountry,
    destinationTypeChange,
    openCreateFlowDialog,
    updateFlow,
    addNewRule,
    save,
    submit,
  };
};
